<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { usePedidoStore } from '@/stores/pedido';
import { useFormaPagamentoStore } from '@/stores/formaPagamento';
import { useListaRevendaStore } from '@/stores/listaRevendas';
import { useClienteStore } from '@/stores/cliente';
import { useFabricantesStore } from '@/stores/fabricantes';
import { useTipoProdutosStore } from '@/stores/tipoProdutos';
import RevendaCard from '@/components/revendas/RevendaCard.vue';
import TomTomAddressAutocomplete from '@/components/inputs/TomTomAddressAutocomplete.vue';
import EnderecoSemRevendedorModal from '@/components/modals/EnderecoSemRevendedorModal.vue';
import RealizarPedidoModal from '@/components/modals/RealizarPedidoModal.vue';
import { estadosOptions } from '@/utils/filtros';
import useFormatters from '@/composables/useFormatters';

const formaPagamentoStore = useFormaPagamentoStore();
const listaRevendasStore = useListaRevendaStore();
const clienteStore = useClienteStore();
const fabricantesStore = useFabricantesStore();
const tipoProdutosStore = useTipoProdutosStore();
const { formatMoeda } = useFormatters();

// Estados da página
const loading = ref(false);
const enderecoPesquisa = ref('');
const marcaSelecionada = ref('0');
const produtoSelecionado = ref('1');
const ordenacao = ref('1');
const resultados = ref([]);
const revendaSelecionada = ref(null);
const modalDetalhes = ref(false);
const externa = ref(false);

// Estados para busca de cliente
const telefonePesquisa = ref('');
const expansionPanels = ref([0]);
const clienteLoading = computed(() => clienteStore.loading);
const clienteError = computed(() => clienteStore.error);
const clienteEncontrado = computed(() => clienteStore.cliente);
const showOpenOrders = ref(false);

// Opções para os selects
const marcaOptions = computed(() => fabricantesStore.fabricantes);
const produtoOptions = computed(() => tipoProdutosStore.tipoProdutos);
const loadingMarcas = computed(() => fabricantesStore.loading);
const loadingProdutos = computed(() => tipoProdutosStore.loading);

const ordenacaoOptions = ref([
  { title: 'Mais Perto', value: '0' },
  { title: 'Menor Preço', value: '1' },
  { title: 'Maior Preço', value: '2' },
  { title: 'Melhor Avaliação', value: '3' }
]);

const isBuscarClienteDisabled = computed(() => {
  return !telefonePesquisa.value || telefonePesquisa.value.trim().length < 15;
});

// Variável para armazenar os detalhes do endereço selecionado
const enderecoSelecionado = ref(null);

// Estados para o snackbar de notificação
const showCopyNotification = ref(false);
const copyMessage = ref('');

// Estados para os modais
const modalCadastroEndereco = ref(false);
const modalRealizarPedido = ref(false);

// Helper function to map full state name to abbreviation
const getStateAbbreviation = (stateName) => {
  const state = estadosOptions.find(option => option.title.toLowerCase() === stateName.toLowerCase());
  return state ? state.value : stateName;
};

// Função chamada quando um endereço é selecionado no autocomplete TomTom
const onPlaceSelected = (place) => {
  console.log('Endereço selecionado (TomTom):', place);
  enderecoSelecionado.value = place;
  enderecoPesquisa.value = place.formattedAddress;

  // Se tiver coordenadas, já realizar a busca automática de revendas
  if (place && place.latitude && place.longitude) {
    pesquisarRevendas();
  }
};

// Função chamada quando o input é limpo
const onClear = () => {
  enderecoSelecionado.value = null;
  resultados.value = [];
};

// Methods para busca de cliente
const buscarCliente = async () => {
  if (telefonePesquisa.value.trim() === '') return;
  
  await clienteStore.buscarClientePorTelefone(telefonePesquisa.value);
  
  if (clienteStore.cliente) {
    // Mostrar automaticamente os pedidos em aberto se existirem
    if (clienteStore.temPedidosAbertos()) {
      showOpenOrders.value = true;
    }
  }
};

const limparCliente = () => {
  telefonePesquisa.value = '';
  clienteStore.limparCliente();
  showOpenOrders.value = false;
};

// Methods para pesquisa de revendas
const pesquisarRevendas = async () => {
  if (enderecoPesquisa.value.trim() === '') return;
  
  loading.value = true;
  
  // Limpar resultados anteriores sempre que iniciar uma nova busca
  resultados.value = [];
  
  try {
    // Usar os detalhes do endereço selecionado se disponíveis
    const endereco = enderecoSelecionado.value ? {
      rua: enderecoSelecionado.value.logradouro,
      numero: enderecoSelecionado.value.numero,
      bairro: enderecoSelecionado.value.bairro,
      cidade: enderecoSelecionado.value.cidade,
      estado: getStateAbbreviation(enderecoSelecionado.value.estado),
      cep: enderecoSelecionado.value.cep,
      latitude: enderecoSelecionado.value.latitude,
      longitude: enderecoSelecionado.value.longitude
    } : {};
    
    // Filtros baseados na seleção do usuário
    const filtros = {
      tipo_produto: produtoSelecionado.value,
      marca: marcaSelecionada.value,
      ordem: ordenacao.value,
      externa: externa.value ? '1' : '0'
    };
    
    // Fazer a chamada da API
    const response = await listaRevendasStore.buscarRevendasPorRegiao(endereco, filtros);
    
    if (response) {
      resultados.value = response;
    } else {
      resultados.value = [];
    }
  } catch (error) {
    console.error('Erro ao pesquisar revendas:', error);
    resultados.value = [];
  } finally {
    loading.value = false;
  }
};

const realizarPedido = (revenda) => {
  revendaSelecionada.value = revenda;
  modalRealizarPedido.value = true;
};

// Função para fechar o modal de realização de pedido
const fecharModalRealizarPedido = () => {
  modalRealizarPedido.value = false;
};

// Função para lidar com o sucesso da realização do pedido
const onPedidoSuccess = (resultado) => {
  console.log('Pedido realizado com sucesso:', resultado);
};

const fecharModalDetalhes = () => {
  modalDetalhes.value = false;
  revendaSelecionada.value = null;
};

const formatarParcelas = (revenda) => {
  if (!revenda.parcelamento || !revenda.qtdParcela) return '';
  
  const qtd = revenda.qtdParcela.qtdparcela;
  const valor = revenda.parcelamento.parcelamento[qtd - 1];
  
  return `${qtd}x de ${formatMoeda(valor)}`;
};

// Função para formatar o tempo de entrega
const formatarTempoEntrega = (item) => {
  if (item.tempo_entrega1 && item.tempo_entrega2) {
    return `${item.tempo_entrega1} min a ${item.tempo_entrega2} min`;
  } else {
    return 'Não informado';
  }
};

// Função para formatar o tempo médio calculado
const formatarTempoMedio = (item) => {
  if (item.tempo_medio_calculo) {
    return `~${item.tempo_medio_calculo} min`;
  } else {
    return 'Não disponível';
  }
};

// Função para copiar texto para a área de transferência com feedback visual
const copyToClipboard = async (data) => {
  try {
    const text = data.text || 'Texto';
    const value = data.value || data;
    
    await navigator.clipboard.writeText(value);
    
    copyMessage.value = `${text} copiado com sucesso!`;
    showCopyNotification.value = true;
    
  } catch (err) {
    console.error('Erro ao copiar para área de transferência:', err);
    copyMessage.value = 'Erro ao copiar para a área de transferência';
    showCopyNotification.value = true;
  }
};

// Função para abrir o modal de cadastro de endereço sem revendedor
const abrirModalCadastroEndereco = () => {
  modalCadastroEndereco.value = true;
};

// Função para fechar o modal de cadastro de endereço
const fecharModalCadastroEndereco = () => {
  modalCadastroEndereco.value = false;
};

// Função para lidar com o sucesso do cadastro
const onCadastroEnderecoSuccess = () => {
  console.log('Endereço cadastrado com sucesso');
};

// Handler for copying text from child components
const handleCopyText = (data) => {
  copyToClipboard(data);
};

// Função para tratar erros da API
const onApiError = (error) => {
  console.error('Erro na API TomTom:', error);
  copyMessage.value = error.message || 'Erro ao buscar endereços';
  showCopyNotification.value = true;
};

// Add watchers for filter changes to trigger automatic search
watch([() => marcaSelecionada.value, () => produtoSelecionado.value, () => ordenacao.value, () => externa.value], 
  () => {
    if (enderecoPesquisa.value.trim() !== '' && enderecoSelecionado.value) {
      loading.value = true;
      nextTick(() => {
        pesquisarRevendas();
      });
    }
  }
);

// Inicialização
onMounted(async () => {
  try {
    // Carregar formas de pagamento
    await formaPagamentoStore.fetchFormasPagamento();
    
    // Carregar fabricantes e tipos de produtos
    await Promise.all([
      fabricantesStore.buscarFabricantes(),
      tipoProdutosStore.buscarTipoProdutos()
    ]);
  } catch (err) {
    console.error('Erro ao carregar dados iniciais:', err);
  }
});

// Add a computed property for the primary color
const primaryColor = computed(() => {
  return 'rgb(var(--v-theme-primary))';
});
</script>

<template>
  <div>
    <v-snackbar
      v-model="showCopyNotification"
      :timeout="2000"
      color="success"
      location="top"
    >
      {{ copyMessage }}
    </v-snackbar>
    
    <!-- Busca de cliente por telefone -->
    <v-expansion-panels v-model="expansionPanels">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-account-search</v-icon>
            <span>Buscar Cliente</span>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="telefonePesquisa"
                type="tel"
                label="Celular do cliente"
                placeholder="Ex: (11) 98765-4321"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                prepend-inner-icon="mdi-phone"
                v-mask="'(##) #####-####'"
                clearable
                @keyup.enter="buscarCliente"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="d-flex align-center">
              <v-btn 
                color="primary" 
                @click="buscarCliente"
                :loading="clienteLoading"
                :disabled="isBuscarClienteDisabled"
              >
                <v-icon start>mdi-account-search</v-icon>
                Buscar Cliente
              </v-btn>
              <v-btn 
                v-if="clienteEncontrado"
                icon
                variant="text"
                color="grey"
                class="ml-2"
                @click="limparCliente"
                title="Limpar busca"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          
          <!-- Resultados da busca de cliente -->
          <v-alert
            v-if="clienteError"
            type="error"
            class="mt-3"
            variant="tonal"
          >
            Cliente não encontrado. Verifique o número e tente novamente.
          </v-alert>
          
          <v-card
            v-if="clienteEncontrado"
            class="mt-3"
            variant="outlined"
          >
            <v-card-item>
              <v-card-title>
                <v-row>
                  <v-col cols="12" md="4">
                    {{ clienteEncontrado.nome }}
                  </v-col>
                  <v-col cols="12" md="8" class="d-flex flex-wrap">
                    <v-chip
                      class="mr-2 mb-2"
                      color="success"
                      variant="outlined"
                      prepend-icon="mdi-shopping-outline"
                    >
                      Última compra: {{ clienteStore.formatarData(clienteEncontrado.dtultimacompra) }}
                    </v-chip>
                    <v-chip
                      v-if="clienteEncontrado.ultimo_pedido_id"
                      class="mr-2 mb-2 cursor-pointer"
                      color="info"
                      variant="outlined"
                      prepend-icon="mdi-cart-outline"
                      @click="copyToClipboard({ text: 'Número do pedido', value: clienteEncontrado.ultimo_pedido_id })"
                      title="Clique para copiar o número do pedido"
                    >
                      Último pedido: #{{ clienteEncontrado.ultimo_pedido_id }}
                    </v-chip>
                    <v-chip
                      v-if="clienteEncontrado.merece_atencao"
                      color="warning"
                      variant="outlined"
                      prepend-icon="mdi-alert-circle-outline"
                      class="mb-2"
                    >
                      Cliente merece atenção
                    </v-chip>
                    <v-chip
                      v-if="clienteEncontrado.tem_pedido_aberto"
                      color="error"
                      variant="outlined"
                      prepend-icon="mdi-alert"
                      class="mb-2 cursor-pointer"
                      @click="showOpenOrders = true"
                      title="Clique para ver detalhes"
                    >
                      {{ clienteEncontrado.pedidos_abertos?.length || '0' }} Pedido(s) em aberto
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-subtitle>
                <v-row>
                  <v-col cols="12" md="4">
                    {{ clienteEncontrado.telefone1 }}
                  </v-col>
                  <v-col cols="12" md="8">
                    <p class="d-flex align-center">
                      <strong>Endereço:</strong>
                      <span class="mx-1">{{ clienteStore.formatarEnderecoCompleto(clienteEncontrado) }}</span>
                      <v-icon
                        size="small"
                        color="primary"
                        class="cursor-pointer ml-1"
                        @click="copyToClipboard({ text: 'Endereço do cliente', value: clienteStore.formatarEnderecoCompleto(clienteEncontrado) })"
                        title="Copiar endereço"
                      >
                        mdi-content-copy
                      </v-icon>
                    </p>
                  </v-col>
                </v-row>
              </v-card-subtitle>
            </v-card-item>
            
            <!-- Alerta de pedidos em aberto -->
            <v-expand-transition>
              <div v-if="clienteStore.temPedidosAbertos() && showOpenOrders">
                <v-divider></v-divider>
                <v-card-text class="bg-error-lighten-5">
                  <div class="d-flex align-center flex-wrap">
                    <div class="d-flex align-center">
                      <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                      <div class="font-weight-bold text-error mr-2">
                        Atenção! Este cliente tem pedido(s) em aberto:
                      </div>
                    </div>
                    
                    <v-chip-group class="ma-0 pa-0 d-inline-flex">
                      <v-chip
                        v-for="pedidoId in clienteEncontrado.pedidos_abertos"
                        :key="pedidoId"
                        color="error"
                        variant="outlined"
                        size="small"
                        class="cursor-pointer"
                        @click="copyToClipboard({ text: 'Número do pedido em aberto', value: pedidoId })"
                        title="Clique para copiar o número do pedido"
                      >
                        #{{ pedidoId }}
                      </v-chip>
                    </v-chip-group>
                    
                    <v-spacer></v-spacer>
                    <v-btn 
                      icon="mdi-close" 
                      variant="text" 
                      size="small" 
                      color="error"
                      @click="showOpenOrders = false"
                    ></v-btn>
                  </div>
                </v-card-text>
              </div>
            </v-expand-transition>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Filtros e pesquisa -->
    <v-card class="mt-4 mb-2">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <!-- TomTom Autocomplete -->
            <TomTomAddressAutocomplete
              v-model="enderecoPesquisa"
              label="Endereço de entrega"
              placeholder="Digite um endereço para buscar"
              @place-selected="onPlaceSelected"
              @clear="onClear"
              @copy-text="handleCopyText"
              @api-error="onApiError"
              clearable
            />
          </v-col>

          <!-- Marca -->
          <v-col cols="12" md="2">
            <v-select
              v-model="marcaSelecionada"
              :items="marcaOptions"
              item-title="title"
              item-value="value"
              label="Marca"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              :loading="loadingMarcas || loading"
              :disabled="loadingMarcas"
            ></v-select>
          </v-col>

          <!-- Produto -->
          <v-col cols="12" md="2">
            <v-select
              v-model="produtoSelecionado"
              :items="produtoOptions"
              item-title="title"
              item-value="value"
              label="Produto"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              :loading="loadingProdutos || loading"
              :disabled="loadingProdutos"
            ></v-select>
          </v-col>

          <!-- Ordenação -->
          <v-col cols="12" md="2">
            <v-select
              v-model="ordenacao"
              :items="ordenacaoOptions"
              item-title="title"
              item-value="value"
              label="Ordenar por"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              :loading="loading"
            ></v-select>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12" md="7" sm="6" class="d-flex align-center flex-wrap">
            <div class="legend-item me-2 mb-1" style="background-color: #48fb66;">
              Novo Revendedor
            </div>

            <div class="legend-item me-2 mb-1" style="background-color: #cee5fe;">
              Pré-pago c/ crédito
            </div>

            <div class="legend-item me-2 mb-1" style="background-color: #ff70fe;">
              Pré-pago s/ crédito
            </div>

            <div class="legend-item mb-1" style="background-color: #FFCE00;">
              Mais rápida
            </div>
          </v-col>

          <v-col cols="12" md="2" sm="6" class="d-flex align-center">
            <v-switch
              v-model="externa"
              label="Revendas externas"
              color="primary"
              hide-details
              density="compact"
              :disabled="loading"
              class="mt-0"
              inset
              :true-color="primaryColor"
            ></v-switch>
          </v-col>
          
          <v-col cols="12" md="3" sm="6" class="d-flex justify-end align-center">
            <v-btn 
              color="#096767" 
              prepend-icon="mdi-map-marker-plus"
              @click="abrirModalCadastroEndereco"
              :disabled="loading"
            >
              Sem Revendedor
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Mostrar um indicador de loading enquanto carrega resultados -->
    <v-card v-if="loading" class="text-center py-6">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
        class="mb-4"
      ></v-progress-circular>
      <h3 class="text-h5 mb-2">Buscando revendas disponíveis...</h3>
      <p class="text-body-1 text-grey">
        Aguarde enquanto encontramos as melhores opções para você.
      </p>
    </v-card>

    <!-- Resultados da pesquisa -->
    <v-card v-else-if="resultados.length > 0" :class="{'externa-mode': externa}">
      <v-card-text>
        <v-row>
          <v-col 
            v-for="(revenda, index) in resultados" 
            :key="`${revenda.idrevenda}-${revenda.idproduto}-${index}`" 
            cols="12" sm="6" md="6" lg="4" xl="3"
          >
            <RevendaCard 
              :revenda="revenda" 
              @realizar-pedido="realizarPedido"
              @copy-id="copyToClipboard"
              class="revenda-card"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Mensagem quando não há resultados -->
    <v-card v-else-if="!loading && resultados.length === 0 && enderecoSelecionado" class="text-center py-6">
      <v-icon size="large" color="grey" class="mb-2">mdi-propane-tank</v-icon>
      <h3 class="text-h5 mb-2">Nenhuma revenda encontrada</h3>
      <p class="text-body-1 text-grey mb-4">
        Não encontramos revendas disponíveis para este endereço.
      </p>
      <v-btn 
        color="primary" 
        prepend-icon="mdi-map-marker-plus"
        @click="abrirModalCadastroEndereco"
      >
        Cadastrar endereço sem revendedor
      </v-btn>
    </v-card>

    <!-- Card inicial -->
    <v-card v-else-if="!loading && !enderecoSelecionado" class="text-center py-8">
      <v-icon size="x-large" color="primary" class="mb-4">mdi-map-search</v-icon>
      <h2 class="text-h4 mb-2">Encontre revendas próximas</h2>
      <p class="text-body-1 mb-4 max-width-500 mx-auto">
        Digite o endereço para encontrar revendas de gás próximas usando a API TomTom.
      </p>
    </v-card>

    <!-- Modal de detalhes da revenda -->
    <v-dialog v-model="modalDetalhes" max-width="700">
      <v-card v-if="revendaSelecionada">
        <v-card-title class="text-h5 bg-primary text-white">
          {{ revendaSelecionada.revendedor }}
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" color="white" @click="fecharModalDetalhes"></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <div class="text-h6 mb-2">Informações da Revenda</div>
              <v-list density="compact">
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-map-marker</v-icon>
                  </template>
                  <v-list-item-title>{{ revendaSelecionada.logradouro }}, {{ revendaSelecionada.numero }}</v-list-item-title>
                  <v-list-item-subtitle>{{ revendaSelecionada.bairro }}, {{ revendaSelecionada.estado }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-clock-outline</v-icon>
                  </template>
                  <v-list-item-title>Horários de Funcionamento</v-list-item-title>
                </v-list-item>
                <v-list-item v-for="(day, index) in revendaSelecionada.days" :key="index" :title="`${day.descricao}: ${day.horainicio} - ${day.horafim}`"></v-list-item>
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-timer</v-icon>
                  </template>
                  <v-list-item-title>Tempo Informado: {{ formatarTempoEntrega(revendaSelecionada) }}</v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-timer-sand</v-icon>
                  </template>
                  <v-list-item-title>Tempo Médio: {{ formatarTempoMedio(revendaSelecionada) }}</v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-star</v-icon>
                  </template>
                  <v-list-item-title>
                    Avaliação: 
                    <v-rating
                      :model-value="Number(revendaSelecionada.avaliacoes_nota_ponderada)"
                      color="amber"
                      density="compact"
                      half-increments
                      readonly
                      size="small"
                    ></v-rating>
                    {{ Number(revendaSelecionada.avaliacoes_nota_ponderada).toFixed(1) }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-map</v-icon>
                  </template>
                  <v-list-item-title>Distância: {{ Number(revendaSelecionada.distance).toFixed(2) }} km</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-h6 mb-2">Informações do Produto</div>
              <v-list density="compact">
                <v-list-item>
                  <template v-slot:prepend>
                    <v-avatar size="24">
                      <v-img :src="`https://tmkt.precodogas.com.br/images/logos/${revendaSelecionada.logomarca}`" 
                            :alt="revendaSelecionada.fabricante" 
                            contain>
                      </v-img>
                    </v-avatar>
                  </template>
                  <v-list-item-title>{{ revendaSelecionada.produto }} {{ revendaSelecionada.peso }}kg</v-list-item-title>
                  <v-list-item-subtitle>{{ revendaSelecionada.fabricante }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-cash</v-icon>
                  </template>
                  <v-list-item-title>Preço: {{ formatMoeda(revendaSelecionada.valor) }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="Number(revendaSelecionada.valordesconto) > 0">
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-sale</v-icon>
                  </template>
                  <v-list-item-title>Desconto: {{ formatMoeda(revendaSelecionada.valordesconto) }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="revendaSelecionada.statusparcela == 1">
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-credit-card</v-icon>
                  </template>
                  <v-list-item-title>Parcelamento: {{ formatarParcelas(revendaSelecionada) }}</v-list-item-title>
                  <v-list-item-subtitle v-if="revendaSelecionada.parcelamento.juros">Com juros</v-list-item-subtitle>
                  <v-list-item-subtitle v-else>Sem juros</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-credit-card-multiple</v-icon>
                  </template>
                  <v-list-item-title>Formas de Pagamento:</v-list-item-title>
                </v-list-item>
                <v-list-item v-for="forma in revendaSelecionada.fpg" :key="forma.id" :title="forma.descricao"></v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <div class="d-flex justify-center">
            <v-btn 
              color="primary"
              size="large"
              prepend-icon="mdi-cart-outline"
              @click="fecharModalDetalhes"
            >
              Finalizar Pedido
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal de cadastro de endereço sem revendedor -->
    <EnderecoSemRevendedorModal
      v-model:show="modalCadastroEndereco"
      :endereco="enderecoSelecionado"
      @close="fecharModalCadastroEndereco"
      @success="onCadastroEnderecoSuccess"
    />

    <!-- Modal para realizar pedido -->
    <RealizarPedidoModal
      v-model:show="modalRealizarPedido"
      :revenda="revendaSelecionada"
      :endereco="enderecoSelecionado"
      @close="fecharModalRealizarPedido"
      @success="onPedidoSuccess"
      @copy-text="handleCopyText"
    />
  </div>
</template>

<style scoped>
.custom-table {
  width: 100%;
}

.max-width-500 {
  max-width: 500px;
}

.cursor-pointer {
  cursor: pointer;
}

.legend-item {
  font-size: 0.9rem;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  border: 1px solid rgba(0,0,0,0.05);
}

.externa-mode {
  border: 2px solid rgb(255, 1, 112);
  background-color: rgba(255, 1, 112, 0.05);
}

:deep(.v-switch__thumb) {
  color: v-bind("externa ? 'rgb(var(--v-theme-primary))' : 'rgba(0, 0, 0, 0.38)'"); 
}

:deep(.v-switch--inset .v-switch__track) {
  opacity: 1;
  background-color: v-bind("externa ? 'rgba(var(--v-theme-primary), 0.3)' : 'rgba(0, 0, 0, 0.12)'");
}

:deep(.v-switch__track:has(+ .v-selection-control--dirty .v-switch__thumb)) {
  opacity: 1;
}

:deep(.v-switch__track:not(:has(+ .v-selection-control--dirty .v-switch__thumb))) {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.20);
}

.text-medium-emphasis {
  opacity: 0.7;
}
</style>
